import {quotationStates,quotationState,quotationStatusTotals} from "../utils/quotationStatusTotals";
import { Eye, Download } from "lucide-react";
import {
  quotationLines,
  quotationPositions,
  quotationProductLabel,
  quotationServices,
} from "../utils/quotationColumns";
import { useSearchParams } from "react-router-dom";
import QuotationWorkflow from "./devis/QuotationWorkflow";
import {
  cachedQuotationList,
  loadQuotationList,
} from "../services/quotationList";
import PageHeading from "../components/PageHeading";
import SearchPending from "../components/SearchPending";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { createListSearch } from "../utils/listSearch";
import { apiErrorMessage } from "../utils/apiError";
import React, { useEffect, useState, useRef } from "react";
import axios from "../axios/AxiosInstance";
import { Modal, message } from "antd";
import { generatePDF } from "../utils/generatePDFUtils";
import logo from "../assets/img/img/logopdf.png";
import toulouse from "../assets/img/img/logotoulouse.png";
import brive from "../assets/img/img/logobrive.png";
import gmail from "../assets/img/img/gmail.png";
import phone from "../assets/img/img/phone.png";
import cgv from "../assets/img/img/cgv-client.jpg";
import {
  lineAmount,
  pricingMultiplier,
  usesQuantity,
} from "../utils/quotationPricing";

const money = (value) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(
    value,
  );
const date = (value) =>
  value && !Number.isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
    : "—";

function QuoteLines({ quotation }) {
  const lines = quotationLines(quotation);
  if (!lines.length)
    return (
      <p className="mb-4 text-sm text-slate-500">
        Aucune prestation sur ce devis.
      </p>
    );
  return (
    <div className="mb-5 overflow-x-auto">
      <p className="mb-2 text-sm font-semibold text-slate-900">
        Prestations ({lines.length})
      </p>
      <table className="w-full text-left text-sm">
        <thead className="bg-slate-100 text-slate-600">
          <tr>
            {["Désignation", "Poste", "Taux", "Qté / coef.", "Montant HT"].map(
              (label) => (
                <th key={label} className="px-3 py-2 font-medium">
                  {label}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.map((item, index) => (
            <tr key={index}>
              <td className="px-3 py-2 font-medium">
                {quotationProductLabel(item) || "—"}
                {item.comment && (
                  <p className="mt-1 text-xs font-normal text-slate-500">
                    {item.comment}
                  </p>
                )}
              </td>
              <td className="px-3 py-2">{item.poste || "—"}</td>
              <td className="px-3 py-2">
                {item.taux != null && item.taux !== "" ? money(item.taux) : "—"}
              </td>
              <td className="px-3 py-2">
                {pricingMultiplier(item) || "—"}
                {usesQuantity(item) ? " unité(s)" : ""}
              </td>
              <td className="px-3 py-2">{money(lineAmount(item))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DevisPage() {
  const [params] = useSearchParams();
  const requestedQuote = params.get("quote");
  const openedQuote = useRef(null);
  const scope = [
    localStorage.getItem("userId"),
    localStorage.getItem("agencyName"),
    localStorage.getItem("accessToken"),
  ].join(":");
  const [managed, setManaged] = useState(null);
  const [stateFilter, setStateFilter] = useState("all");
  const [rows, setRows] = useState(() => cachedQuotationList(scope) || []);
  const [reload, setReload] = useState(0);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, searching] = useDebouncedValue(search);
  const [preview, setPreview] = useState(null);
  const [opening, setOpening] = useState(false);
  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview.url);
    },
    [preview],
  );
  const consult = async (quotation, client, download = false) => {
    if (opening) return;
    setOpening(true);
    try {
      await Promise.all(
        [logo, toulouse, brive, gmail, phone, cgv].map(
          (src) =>
            new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = resolve;
              img.onerror = reject;
              img.src = src;
            }),
        ),
      );
      const url = await generatePDF(
        {
          ...quotation,
          client: {
            ...client,
            ...(typeof quotation.client === "object" ? quotation.client : {}),
          },
        },
        true,
        null,
        "url",
      );
      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.download = `Devis_${quotation.quotationNumber ?? "Provisoire"}.pdf`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else
        setPreview({ url, number: quotation.quotationNumber ?? "Provisoire" });
    } catch (error) {
      message.error(apiErrorMessage(error, "Impossible d’ouvrir le PDF de ce devis. Réessayez."));
    } finally {
      setOpening(false);
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    const cached = reload === 0 ? cachedQuotationList(scope) : null;
    if (cached) {
      setRows(cached);
      setLoading(false);
      return () => controller.abort();
    }
    setLoading(true);
    setError("");
    setProgress(null);
    loadQuotationList(axios, {
      scope,
      signal: controller.signal,
      onProgress: ({ rows, completed, total }) => {
        setRows(rows);
        setProgress({ completed, total });
      },
    })
      .then((result) => {
        if (controller.signal.aborted || !result) return;
        setRows(result.rows);
        if (result.failures)
          setError(
            `${result.failures} client(s) n’ont pas pu être chargés. ${apiErrorMessage(result.error, "Cliquez sur Actualiser pour réessayer.")}`,
          );
      })
      .catch((error) => {
        const detail = apiErrorMessage(error, "Impossible de charger les devis. Cliquez sur Actualiser pour réessayer.");
        if (!controller.signal.aborted && detail) setError(detail);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [scope, reload]);
  useEffect(() => {
    if (!requestedQuote || openedQuote.current === requestedQuote) return;
    const row = rows.find((row) => row.quotation._id === requestedQuote);
    if (row) {
      openedQuote.current = requestedQuote;
      setManaged(row);
    }
  }, [requestedQuote, rows]);
  const updateWorkflow = (quotationId, workflow) =>
    setRows((current) =>
      current.map((row) =>
        row.quotation._id === quotationId
          ? { ...row, quotation: { ...row.quotation, workflow } }
          : row,
      ),
    );
  const matchingRows = rows
    .filter(
      createListSearch(appliedSearch, [
        "quotation.quotationNumber",
        "quotation.type",
        "quotation.status",
        "quotation.createdAt",
        "quotation.products.product",
        "quotation.products.poste",
        "quotation.products.type",
        "client.companyName",
        "client.siret",
        "client.contactPhone",
        "client.contactEMail",
        "client.town",
      ]),
    )
;
  const totals=quotationStatusTotals(matchingRows);
  const visible=matchingRows.filter(({quotation})=>stateFilter==='all'||quotationState(quotation)===stateFilter);

  return (
    <div className="bg-slate-50">
      <PageHeading
        icon="contracts"
        title={<> Devis </>}
        description={<> Retrouvez vos devis par numéro ou par client. </>}
      />
      <div role="group" aria-label="Statuts des devis" className="mt-5 flex flex-wrap gap-2">
        {[{value:'all',label:'Tous'},...quotationStates.map(value=>({value,label:value}))].map(({value,label})=><button key={value} type="button" aria-pressed={stateFilter===value} onClick={()=>setStateFilter(value)} className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${stateFilter===value?'border-slate-800 bg-slate-800 text-white':'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'}`}><span>{label}</span>{totals[value]>0&&<span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${stateFilter===value?'bg-white/15 text-white':'bg-slate-100 text-slate-600'}`}>{totals[value]}</span>}</button>)}
      </div>
      <input
        aria-label="Rechercher un devis"
        placeholder="Numéro de devis ou nom du client…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="my-6 w-full max-w-md rounded-lg border border-slate-300 px-4 py-3"
      />
      <SearchPending pending={searching} />
      {error && (
        <p role="alert" className="mb-4 text-red-700">
          {error}
        </p>
      )}
      <button
        disabled={loading}
        onClick={() => setReload((value) => value + 1)}
        className="mb-4 ml-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
      >
        {loading ? "Actualisation…" : "Actualiser"}
      </button>
      {loading && (
        <p role="status" className="mb-4 text-sm text-slate-500">
          Chargement des devis…{" "}
          {progress
            ? `${progress.completed} / ${progress.total} devis chargés`
            : ""}
        </p>
      )}
      {(!loading || rows.length > 0) && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                {[
                  "Numéro devis",
                  "Client",
                  "Poste",
                  "Service",
                  "Date",
                  "Validité",
                  "Montant HT",
                  "Suivi",
                  "Actions",
                ].map((label) => (
                  <th key={label} className="px-5 py-4">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map(({ quotation, client }, index) => (
                <tr
                  key={quotation._id || `${client._id}-${index}`}
                  className="hover:bg-slate-50"
                >
                  <td className="px-5 py-4 font-semibold text-blue-800">
                    {quotation.quotationNumber ?? "Provisoire"}
                    {quotation.isDraft && (
                      <span className="ml-2 text-xs font-normal text-slate-500">
                        Brouillon
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 font-medium">
                    {client.companyName}
                  </td>
                  <td className="px-5 py-4">{quotationPositions(quotation)}</td>
                  <td className="px-5 py-4">{quotationServices(quotation)}</td>
                  <td className="px-5 py-4">{date(quotation.createdAt)}</td>
                  <td className="px-5 py-4">{date(quotation.expiryDate)}</td>
                  <td className="px-5 py-4">
                    {money(
                      quotationLines(quotation).reduce(
                        (total, item) => total + lineAmount(item),
                        0,
                      ),
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <button
                      type="button"
                      onClick={() => setManaged({ quotation, client })}
                      title="Gérer le suivi du devis"
                      className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold hover:bg-blue-50"
                    >
                      {quotation.workflow?.state ||
                        (quotation.isDraft ? "Brouillon" : "Créé")}
                    </button>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        title="Visionner"
                        aria-label="Visionner"
                        type="button"
                        disabled={opening}
                        onClick={() => consult(quotation, client)}
                        className="rounded-lg p-2 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        title="Télécharger"
                        aria-label="Télécharger"
                        type="button"
                        disabled={opening}
                        onClick={() => consult(quotation, client, true)}
                        className="rounded-lg p-2 text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                      >
                        <Download size={18} />
                      </button>

                    </div>
                  </td>
                </tr>
              ))}
              {!visible.length && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-5 py-10 text-center text-slate-500"
                  >
                    Aucun devis trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        open={Boolean(managed)}
        title={`Suivi du devis n° ${managed?.quotation.quotationNumber ?? "Provisoire"}`}
        width={800}
        footer={null}
        onCancel={() => setManaged(null)}
        destroyOnClose
      >
        {managed && (
          <>
            <QuoteLines quotation={managed.quotation} />
            <QuotationWorkflow
              key={managed.quotation._id}
              quotation={managed.quotation}
              client={managed.client}
              onChange={(workflow) =>
                updateWorkflow(managed.quotation._id, workflow)
              }
              onDownload={() => consult(managed.quotation, managed.client, true)}
            />
          </>
        )}
      </Modal>
      <Modal
        open={Boolean(preview)}
        onCancel={() => setPreview(null)}
        title={`Devis n° ${preview?.number ?? ""}`}
        width={1100}
        centered
        footer={
          preview ? (
            <a
              href={preview.url}
              download={`Devis_${preview.number}.pdf`}
              className="inline-block rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white"
            >
              Télécharger le PDF
            </a>
          ) : null
        }
      >
        {preview && (
          <iframe
            title={`PDF du devis n° ${preview.number}`}
            src={preview.url}
            className="h-[70vh] w-full rounded-lg border border-slate-200"
          />
        )}
      </Modal>
    </div>
  );
}
